// Pure memory-name helpers — zero dependencies.
//
// Split out of memories.js (which pulls in the analytics-query stack, and
// transitively shell.js's module-load `window` access) so memoryBodies.js —
// and the unit tests — can use the extractor without dragging the whole query
// layer (or a DOM) into a node test env. memories.js re-exports everything
// here, so existing importers are unaffected. Mirrors the mcpServerNames.js /
// mcpServers.js split exactly.
//
//   • extractMemoryFromPath — the memory FILES a harness read/wrote, sliced out
//     of PROMPT_RESOURCE_PATHS (feeds the sidebar dimension, like skills; also
//     used by memoryBodies.js to attribute Read/Write content to a slug).
//   • labelForMemory — display label for a slug.

// Pull the memory SLUG out of a resource path, or null if the path isn't a
// Claude Code auto-memory file.
//
// Claude Code stores per-user memory as flat markdown under a project's
// `.claude` home — `…/.claude/projects/<project-slug>/memory/<slug>.md`
// (the index is `MEMORY.md`; each fact is its own `<slug>.md`). The id is the
// filename without `.md` — `MEMORY`, `feedback_no_italic`, `project_scope_tree`.
//
// Three precision filters, all load-bearing (the resource path is the ONLY
// signal — there's no "memory" column on the wire):
//   • a `.claude` ancestor segment — rejects repo `memory/` source dirs and
//     any unrelated path that merely has a `memory/` segment.
//   • a SINGULAR `memory` segment — this is what distinguishes Claude Code
//     (`.claude/.../memory/`) from Codex (`.codex/memories/`, plural). The
//     string `memories/` never contains the substring `memory/`, so the two
//     can't be confused.
//   • a `.md` leaf directly under `memory/` (the file must be the last
//     segment) — rejects deeper nestings and non-markdown siblings.
//
//   /Users/x/.claude/projects/-Users-x-origin/memory/MEMORY.md          → "MEMORY"
//   C:/Users/x/.claude/projects/D--git-origin/memory/feedback_voice.md  → "feedback_voice"
//   /repo/src/memory/store.md                                           → null  (no .claude)
//   /Users/x/.codex/memories/MEMORY.md                                 → null  (plural / no .claude)
//   /Users/x/.claude/projects/p/memory/sub/deep.md                     → null  (not a flat leaf)
export function extractMemoryFromPath(path) {
    const norm = String(path ?? "").replace(/\\/g, "/");
    if (!/\/memory\//i.test(norm)) return null;
    const segs = norm.split("/").filter(Boolean);
    const i = segs.findIndex((seg) => seg.toLowerCase() === "memory");
    if (i === -1) return null;
    // Require a `.claude` ancestor — the precision anchor (see header).
    if (!segs.slice(0, i).some((seg) => seg === ".claude")) return null;
    // Leaf must be the file directly under memory/ (flat) and end in `.md`.
    if (i + 1 !== segs.length - 1) return null;
    const leaf = segs[i + 1];
    if (!leaf || !/\.md$/i.test(leaf)) return null;
    const slug = leaf.replace(/\.md$/i, "");
    // Guard malformed elements that slice down to a non-name token (e.g. a
    // bare `,` from a serialized empty array — String(["",""]) === ",").
    if (!/[a-z0-9]/i.test(slug) || slug.includes(",")) return null;
    return slug;
}

// Given a memory file's full resource path, the path of the index (MEMORY.md)
// in the SAME project directory — `…/memory/<slug>.md` → `…/memory/MEMORY.md`.
// Lets a leaf's orphan/backlink check anchor on its OWN project's index rather
// than any MEMORY.md on the endpoint (which conflates a user's multiple
// projects — they share filenames but live under different `…/projects/<slug>/`
// dirs). Returns null when the path carries no `/memory/` segment. Operates on
// the raw stored form (forward-slash; Windows paths arrive as `C:/…`).
export function indexPathForMemoryPath(path) {
  const s = String(path ?? "");
  const i = s.lastIndexOf("/memory/");
  if (i === -1) return null;
  return `${s.slice(0, i)}/memory/MEMORY.md`;
}

// Reject resource_paths that aren't real files — e.g. a leaked Rust raw-string
// prefix (`r'C:/…`) or other junk in the array (the known resource_paths
// data-quality gap). A real memory path starts at a filesystem root.
export function isLikelyRealMemoryPath(path) {
  const s = String(path ?? "").replace(/\\/g, "/").trim();
  return /^(?:[A-Za-z]:\/|\/|~)/.test(s);
}

// ── Project hierarchy ──────────────────────────────────────────────
// Memory files live under `…/.claude/projects/<project-slug>/memory/<file>.md`.
// The `<project-slug>` (Claude Code's encoded working-dir) is the natural parent
// level, so "memories" is a 2-level hierarchy: project → file. These helpers
// derive the project from a path so the dim can group/scope by it (the same
// filename in two projects must NOT collapse — the O-2327 conflation).

// Project DIRECTORY for a memory file path — everything up to (not incl)
// `/memory/`. `…/projects/D--git-origin/memory/MEMORY.md` → `…/projects/D--git-origin`.
export function memoryProjectDirFromPath(path) {
  const s = String(path ?? "");
  const i = s.lastIndexOf("/memory/");
  return i === -1 ? null : s.slice(0, i);
}

// True when a memory scope id is a PROJECT node (a project dir) rather than a
// leaf file path or a bare slug. Project dirs live under `/projects/` and do NOT
// end in `/memory/<file>.md`.
export function isMemoryProjectId(id) {
  const s = String(id ?? "");
  if (!s.includes("/")) return false;                  // bare slug
  if (/\/memory\/[^/]+\.md$/i.test(s)) return false;   // a leaf file path
  return /\/projects\//.test(s) || /\/memory$/i.test(s);
}

// The Claude Code project slug (encoded workdir) for a project dir.
//   `…/projects/D--git-origin` → `D--git-origin`
export function memoryProjectSlug(projectDir) {
  const s = String(projectDir ?? "").replace(/\\/g, "/");
  const m = /\/projects\/([^/]+)/.exec(s);
  return m ? m[1] : (s.split("/").filter(Boolean).pop() || s);
}

// Friendly label for a project dir — the LAST TWO path components of the encoded
// workdir slug (Claude Code joins path segments with `-`). Two components, not
// one, so sibling projects that share a leaf name stay distinguishable instead of
// all collapsing to one ambiguous label — the "repeated origin" problem when a
// person has `…/git/origin`, `…/projects/origin`, `…/origin/origin`. Best-effort
// and non-reversible by design; the unique id stays the full dir.
//   D--git-origin → "git/origin"   -Users-k-origin-origin → "origin/origin"
//   -Users-x-projects-origin → "projects/origin"   D-- → "D"
export function memoryProjectLabel(projectDir) {
  const toks = String(memoryProjectSlug(projectDir)).split("-").filter(Boolean);
  return toks.length ? toks.slice(-2).join("/") : String(memoryProjectSlug(projectDir));
}

// The index file's reserved slug — the project's `MEMORY.md`, which is the index
// of a store, not a memory in it. Folded out of the flat "all memories" list (it
// would otherwise appear once per project as a wall of identical "Memory index"
// rows) and out of per-person memory counts.
export const MEMORY_INDEX_SLUG = "MEMORY";
export function isMemoryIndexSlug(slug) {
  return slug === MEMORY_INDEX_SLUG;
}

// Coarse memory TYPE for table display, inferred from the slug's conventional
// prefix (the harness writes `project_*`, `feedback_*`, `reference_*`, `user_*`,
// `env_*`). Best-effort — slugs without a known prefix read as "Note".
const MEMORY_KIND_BY_PREFIX = {
  project: "Project",
  feedback: "Feedback",
  reference: "Reference",
  user: "User",
  env: "Environment",
};
export function memoryKindFromSlug(slug) {
  if (isMemoryIndexSlug(slug)) return "Index";
  const prefix = String(slug ?? "").split(/[_-]/)[0]?.toLowerCase();
  return MEMORY_KIND_BY_PREFIX[prefix] || "Note";
}

// The slug doubles as the id; prettify the index file's reserved name and
// project-node ids. The sidebar resolves labels through this adapter `labelFor`
// (not the entry's own label), so project dirs must friendly-ify here too.
export function labelForMemory(id) {
    if (!id) return "";
    if (id === "MEMORY") return "Memory index";
    if (isMemoryProjectId(id)) return memoryProjectLabel(id);
    return String(id);
}
